import React, { useMemo, useState } from "react";
import PaddingPrepPill from "./PaddingPrepPill";
import CustomColorPicker from "../../../../../components/shared/CustomColorPicker";
import {
  clampPaddingSide,
  PADDING_UNIT,
} from "../../../../../utils/canvasPaddingLayout";
import "./PaddingPrepControls.css";

const FILL_SWATCHES = [
  { id: "transparent", color: "transparent", label: "Transparent" },
  { id: "black", color: "#000000", label: "Black" },
  { id: "white", color: "#FFFFFF", label: "White" },
  { id: "green", color: "#22C55E", label: "Green" },
  { id: "red", color: "#EF4444", label: "Red" },
  { id: "blue", color: "#2563EB", label: "Blue" },
];

function normalizeFillColor(color) {
  if (!color || color === "transparent") return "transparent";
  return String(color).trim().toLowerCase();
}

const PaddingPrepControls = ({ settings, onChange, disabled = false }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const unit = settings.unit === PADDING_UNIT.PX ? PADDING_UNIT.PX : PADDING_UNIT.PERCENT;
  const unitLabel = unit === PADDING_UNIT.PX ? "px" : "%";
  const cornerMax = unit === PADDING_UNIT.PX ? 200 : 50;
  const cornerRadius = Number(settings.cornerRadius) || 0;

  const [cornerDraft, setCornerDraft] = useState(null);

  const patch = (partial) => {
    if (disabled) return;
    onChange({ ...settings, ...partial });
  };
  const setSide = (side, value) => patch({ [side]: value });

  const setUnit = (nextUnit) => {
    if (nextUnit === unit) return;
    patch({ unit: nextUnit });
  };

  const fillColor = settings.fillColor ?? "transparent";
  const normalizedFill = normalizeFillColor(fillColor);
  const isTransparent = normalizedFill === "transparent";
  const customFillActive =
    !isTransparent &&
    !FILL_SWATCHES.some(
      (s) => normalizeFillColor(s.color) === normalizedFill
    );

  const sliderFillPct = useMemo(() => {
    if (!cornerMax) return 0;
    return Math.max(0, Math.min(100, (cornerRadius / cornerMax) * 100));
  }, [cornerMax, cornerRadius]);

  const setCornerRadius = (raw, { fromSlider = false } = {}) => {
    if (fromSlider) {
      setCornerDraft(null);
      patch({
        cornerRadius: clampPaddingSide(Number(raw) || 0, unit, cornerMax),
      });
      return;
    }

    const val = String(raw).replace(/[^0-9]/g, "").slice(0, 4);
    if (val === "") {
      setCornerDraft("");
      return;
    }
    setCornerDraft(null);
    patch({
      cornerRadius: clampPaddingSide(Number(val), unit, cornerMax),
    });
  };

  const commitCornerDraft = () => {
    if (cornerDraft === "") {
      patch({ cornerRadius: 0 });
    }
    setCornerDraft(null);
  };

  const cornerInputValue = cornerDraft ?? String(cornerRadius);

  return (
    <div
      className={`padding-prep-controls${
        disabled ? " padding-prep-controls--disabled" : ""
      }`}
    >
      <section className="padding-prep-controls__block">
        <div className="padding-prep-controls__head">
          <div className="padding-prep-controls__title">Padding</div>
          <div
            className="padding-prep-controls__unit-toggle"
            role="group"
            aria-label="Padding unit"
          >
            <button
              type="button"
              className={`padding-prep-controls__unit-btn${
                unit === PADDING_UNIT.PERCENT ? " is-active" : ""
              }`}
              onClick={() => setUnit(PADDING_UNIT.PERCENT)}
              disabled={disabled}
            >
              %
            </button>
            <button
              type="button"
              className={`padding-prep-controls__unit-btn${
                unit === PADDING_UNIT.PX ? " is-active" : ""
              }`}
              onClick={() => setUnit(PADDING_UNIT.PX)}
              disabled={disabled}
            >
              px
            </button>
          </div>
        </div>

        <div className="padding-prep-controls__grid">
          <PaddingPrepPill
            direction="top"
            value={settings.top}
            unit={unit}
            onChange={(v) => setSide("top", v)}
            disabled={disabled}
          />
          <PaddingPrepPill
            direction="right"
            value={settings.right}
            unit={unit}
            onChange={(v) => setSide("right", v)}
            disabled={disabled}
          />
          <PaddingPrepPill
            direction="bottom"
            value={settings.bottom}
            unit={unit}
            onChange={(v) => setSide("bottom", v)}
            disabled={disabled}
          />
          <PaddingPrepPill
            direction="left"
            value={settings.left}
            unit={unit}
            onChange={(v) => setSide("left", v)}
            disabled={disabled}
          />
        </div>
      </section>

      <section className="padding-prep-controls__block">
        <div className="padding-prep-controls__section-title">Padding color</div>
        <div className="padding-prep-controls__swatches">
          {FILL_SWATCHES.map((swatch) => {
            const active =
              swatch.id === "transparent"
                ? isTransparent
                : normalizeFillColor(swatch.color) === normalizedFill;
            return (
              <button
                key={swatch.id}
                type="button"
                className={`padding-prep-controls__swatch${
                  active ? " is-active" : ""
                }${swatch.id === "transparent" ? " padding-prep-controls__swatch--checker" : ""}`}
                style={
                  swatch.id === "transparent"
                    ? undefined
                    : { backgroundColor: swatch.color }
                }
                aria-label={swatch.label}
                aria-pressed={active}
                disabled={disabled}
                onClick={() => patch({ fillColor: swatch.color })}
              />
            );
          })}
          <button
            type="button"
            className={`padding-prep-controls__swatch padding-prep-controls__swatch--custom${
              customFillActive ? " is-active" : ""
            }`}
            style={customFillActive ? { backgroundColor: fillColor } : undefined}
            aria-label="Custom color"
            aria-pressed={customFillActive}
            disabled={disabled}
            onClick={() => !disabled && setShowColorPicker(true)}
          >
            +
          </button>
        </div>
      </section>

      <section className="padding-prep-controls__block">
        <div className="padding-prep-controls__slider-field">
          <div className="padding-prep-controls__slider-head">
            <span className="padding-prep-controls__section-title">Round corners</span>
            <div className="padding-prep-controls__slider-value">
              <input
                type="text"
                inputMode="numeric"
                className="padding-prep-controls__slider-input"
                value={cornerInputValue}
                aria-label="Corner radius"
                disabled={disabled}
                onChange={(e) => setCornerRadius(e.target.value)}
                onBlur={commitCornerDraft}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    commitCornerDraft();
                    e.currentTarget.blur();
                  }
                }}
              />
              <span className="padding-prep-controls__slider-suffix">{unitLabel}</span>
            </div>
          </div>
          <input
            type="range"
            className="padding-prep-controls__slider"
            min={0}
            max={cornerMax}
            step={1}
            value={cornerRadius}
            aria-label="Corner radius slider"
            disabled={disabled}
            style={{ "--padding-slider-fill": `${sliderFillPct}%` }}
            onChange={(e) => setCornerRadius(e.target.value, { fromSlider: true })}
          />
        </div>
      </section>

      <CustomColorPicker
        isOpen={showColorPicker}
        onClose={() => setShowColorPicker(false)}
        color={isTransparent ? "#FFFFFF" : fillColor}
        onChange={(color) => patch({ fillColor: color })}
        onApply={(color) => {
          patch({ fillColor: color });
          setShowColorPicker(false);
        }}
        anchorSelector='.tool-prep-popup[data-tool="padding"] .tool-prep-popup__left'
        anchorGap={10}
      />
    </div>
  );
};

export default PaddingPrepControls;
